/**
 * TrackingNumberBadge - Displays a hard-copy tracking number with status dot and copy button.
 */

import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { TRACKING_STATUS_CONFIG, type HardCopyTrackingStatus } from '@/types/hardCopyTracking';

interface TrackingNumberBadgeProps {
  trackingNumber: string;
  status: HardCopyTrackingStatus;
  size?: 'sm' | 'md' | 'lg';
  showCopy?: boolean;
}

export function TrackingNumberBadge({
  trackingNumber,
  status,
  size = 'md',
  showCopy = true,
}: TrackingNumberBadgeProps) {
  const [copied, setCopied] = useState(false);
  const config = TRACKING_STATUS_CONFIG[status];

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(trackingNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = trackingNumber;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Badge
      variant={config.badgeVariant as any}
      size={size}
      dot
      className="font-mono gap-1.5"
    >
      {trackingNumber}
      {showCopy && (
        <button
          onClick={handleCopy}
          className="ml-0.5 hover:opacity-70 transition-opacity"
          title="Copy tracking number"
        >
          {copied ? (
            <Check className="w-3 h-3" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </button>
      )}
    </Badge>
  );
}
